import { defineQuery, useInfiniteQuery, useQueryCache } from '@pinia/colada'
import { computed, ref } from 'vue'
import { storeToRefs } from 'pinia'
import { GetAllTradeHistoryDTO } from '@trading/core'
import type { ID, TradeHistoryTrade } from '@trading/core'
import type { UnixDateRange } from '~/components/features/TradeHistoryTable/utils/dateRange'

export const TRADE_HISTORY_QUERY_KEY = 'tradeHistory'

export function getTradeHistoryQueryKey(
  accountId: ID,
  openDateRange: UnixDateRange | null,
  closeDateRange: UnixDateRange | null
) {
  return [
    TRADE_HISTORY_QUERY_KEY,
    String(accountId),
    openDateRange?.from ?? null,
    openDateRange?.to ?? null,
    closeDateRange?.from ?? null,
    closeDateRange?.to ?? null
  ]
}

export const useTradeHistoryQuery = defineQuery(() => {
  const { $wsPositionService } = useNuxtApp()
  const tradeHistoryTableStore = useTradeHistoryTableStore()
  const { openDateRange, closeDateRange } = storeToRefs(tradeHistoryTableStore)
  const queryCache = useQueryCache()
  const accountId = ref<ID | null>(null)

  const getQueryKey = () => {
    if (accountId.value == null) {
      return [TRADE_HISTORY_QUERY_KEY, 'empty']
    }

    return getTradeHistoryQueryKey(accountId.value, openDateRange.value, closeDateRange.value)
  }

  const query = useInfiniteQuery({
    key: () => getQueryKey(),
    // enabled, пока не выбран аккаунт — запрос не стартует
    enabled: () => accountId.value != null,
    initialPageParam: null as string | null,
    query: ({ pageParam }) =>
      $wsPositionService.getAllTrades(
        new GetAllTradeHistoryDTO(
          accountId.value!,
          pageParam ?? undefined,
          openDateRange.value?.from,
          openDateRange.value?.to,
          closeDateRange.value?.from,
          closeDateRange.value?.to
        )
      ),
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    staleTime: 60_000
  })

  const trades = computed(() => {
    return query.data.value?.pages.flatMap((page) => page.trades) ?? []
  })

  function changeHistoryTrade(
    newHistoryItem: TradeHistoryTrade | null = {
      id: '97f477eb-33c4-4e2a-8e6b-e7a93d4d0f2d',
      account_id: '1544562',
      symbol: 'BTCUSD.N',
      side: 'LONG',
      price: '1',
      quantity: '1',
      fee: '0E-18',
      fee_currency: 'USD',
      is_maker: false,
      realized_pnl: '3.010000000000000000',
      sl: '',
      tp: '',
      swap: '0E-18',
      open_time: '2026-09-02T11:03:26.595000Z',
      close_time: '2026-09-03T09:59:18.341044Z',
      trade_time: '2026-09-03T09:59:18.341044Z'
    }
  ) {
    // queryCache.invalidateQueries({
    //   key: getQueryKey()
    // })
    queryCache.setQueryData(getQueryKey(), (oldData) => {
      if (!oldData || !newHistoryItem) return oldData
      const newValue = oldData
      // @ts-ignore
      newValue.pages = newValue.pages.map((page) => {
        const newpage = page
        // @ts-ignore
        newpage.trades = newpage.trades.map((t) => {
          if (t.id === newHistoryItem.id) {
            return { ...t, isChanged: true }
          } else {
            return t
          }
        })

        return newpage
      })
      return newValue
    })
  }

  const summary = computed(() => {
    const pages = query.data.value?.pages
    return pages?.[pages.length - 1]?.summary ?? null
  })

  return {
    ...query,
    accountId,
    changeHistoryTrade,
    trades,
    summary
  }
})
